/**
 * Radiology Voice Dictation & Command Abstraction Service
 * Decouples speech-to-text processing from reporting UI, allowing
 * future plug-and-play voice dictation engines (e.g. Nuance Dragon Medical,
 * AWS Transcribe Medical, Whisper API, or Browser Web Speech API).
 */

class RadiologyVoiceService {
  constructor() {
    this.recognition = null;
    this.isListening = false;
    this.callbacks = {
      onTranscript: null,
      onCommand: null,
      onError: null,
      onStateChange: null
    };

    this.initSpeechEngine();
  }

  initSpeechEngine() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = true;
      this.recognition.interimResults = true;
      this.recognition.lang = 'en-US';

      this.recognition.onresult = (event) => this.handleResult(event);
      this.recognition.onerror = (event) => this.handleError(event);
      this.recognition.onend = () => {
        if (this.isListening) {
          // Keep alive if listening
          try { this.recognition.start(); } catch (_) {}
        } else {
          this.notifyState(false);
        }
      };
    }
  }

  isSupported() {
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  }

  start({ onTranscript, onCommand, onError, onStateChange }) {
    this.callbacks = { onTranscript, onCommand, onError, onStateChange };

    if (!this.recognition) {
      if (onError) onError('Speech recognition is not natively supported in this browser. Running in simulated demonstration mode.');
      return false;
    }

    try {
      this.isListening = true;
      this.recognition.start();
      this.notifyState(true);
      return true;
    } catch (err) {
      if (onError) onError(err.message || 'Could not start voice dictation.');
      this.isListening = false;
      this.notifyState(false);
      return false;
    }
  }

  stop() {
    this.isListening = false;
    if (this.recognition) {
      try { this.recognition.stop(); } catch (_) {}
    }
    this.notifyState(false);
  }

  stopListening() {
    return this.stop();
  }

  startListening(options) {
    return this.start(options || {});
  }

  notifyState(active) {
    if (this.callbacks.onStateChange) {
      this.callbacks.onStateChange(active);
    }
  }

  handleResult(event) {
    let interim = '';
    let finalTranscript = '';

    for (let i = event.resultIndex; i < event.results.length; ++i) {
      const transcript = event.results[i][0].transcript;
      if (event.results[i].isFinal) {
        finalTranscript += transcript;
      } else {
        interim += transcript;
      }
    }

    const text = (finalTranscript || interim).trim();
    if (!text) return;

    // Check for voice commands
    const command = this.parseCommand(text.toLowerCase());
    if (command) {
      if (this.callbacks.onCommand) {
        this.callbacks.onCommand(command.name, command.label);
      }
      return;
    }

    if (this.callbacks.onTranscript) {
      this.callbacks.onTranscript(finalTranscript || interim, !!finalTranscript);
    }
  }

  parseCommand(text) {
    const commands = [
      { trigger: 'next section', name: 'NEXT_SECTION', label: 'Next Section' },
      { trigger: 'insert normal macro', name: 'INSERT_NORMAL', label: 'Insert Normal Macro' },
      { trigger: 'insert normal', name: 'INSERT_NORMAL', label: 'Insert Normal Macro' },
      { trigger: 'mark normal', name: 'INSERT_NORMAL', label: 'Insert Normal Macro' },
      { trigger: 'insert renal macro', name: 'INSERT_RENAL', label: 'Insert Renal Macro' },
      { trigger: 'mark critical', name: 'MARK_CRITICAL', label: 'Mark Critical Finding' },
      { trigger: 'mark critical finding', name: 'MARK_CRITICAL', label: 'Mark Critical Finding' },
      { trigger: 'flag critical', name: 'MARK_CRITICAL', label: 'Mark Critical Finding' },
      { trigger: 'flag critical finding', name: 'MARK_CRITICAL', label: 'Mark Critical Finding' },
      { trigger: 'save draft', name: 'SAVE_DRAFT', label: 'Save Draft' },
      { trigger: 'run quality check', name: 'QUALITY_CHECK', label: 'Run Quality Check' },
      { trigger: 'quality check', name: 'QUALITY_CHECK', label: 'Run Quality Check' }
    ];

    for (const cmd of commands) {
      if (text.includes(cmd.trigger)) {
        return {
          ...cmd,
          command: cmd.name
        };
      }
    }
    return null;
  }

  handleError(event) {
    if (event.error === 'no-speech') return; // Ignore silent pauses
    if (this.callbacks.onError) {
      this.callbacks.onError(event.error || 'Speech recognition encountered an error.');
    }
  }

  /**
   * Diagnostic simulation method for headless automated testing
   */
  simulateVoiceInput(phrase) {
    const command = this.parseCommand(phrase.toLowerCase());
    if (command && this.callbacks.onCommand) {
      this.callbacks.onCommand(command.name, command.label);
    } else if (this.callbacks.onTranscript) {
      this.callbacks.onTranscript(phrase, true);
    }
  }
}

const radiologyVoiceService = new RadiologyVoiceService();
export default radiologyVoiceService;
